class HeaderMegamenu extends HTMLElement {
  constructor() {
    super();

    // Bind
    this.openMegaMenu = this.openMegaMenu.bind(this);
    this.closeMegaMenu = this.closeMegaMenu.bind(this);
    this.maybeCloseMegaMenu = this.maybeCloseMegaMenu.bind(this);
    this.onScroll = this.onScroll.bind(this);

    // State
    this.isOverOpener = false;
    this.isOverMenu = false;
    this.closeDelayMs = 120;
    this.closeTimer = null;
    this.openTab = null;

    // Scroll close settings
    this.scrollCloseThreshold = 12;
    this._lastScrollY = window.scrollY || 0;
  }

  connectedCallback() {
    this.overLay = document.querySelector("#headerMegamenuOverlay");
    // External openers (siblings/parent area)
    this.menuOpeners =
      this.parentElement?.querySelectorAll?.(
        ".ep__level-one-link[data-haschildren]"
      ) || [];

    // Menu tabs
    this.menuTabs = Array.from(
      this.querySelectorAll(".ep__header-megamenu-tab")
    );

    // Optional hover area that keeps menu open while hovered
    this.hoverArea =
      document.querySelector(".ep__header-link-hover-area") || null;

    // Accessibility (minimal)
    this.setAttribute("role", "region");
    this.setAttribute("aria-label", "Site menu");
    this.setAttribute("aria-hidden", "true");

    this.attachEvents();

    // Initial closed state
    this.setAttribute("aria-hidden", "true");
    this.classList.remove("open");
  }

  getContentHeight(element) {
    if (!(element instanceof HTMLElement)) {
      throw new Error("Argument must be an HTML element");
    }

    // scrollHeight gives the total content height,
    // including overflow that isn't visible.
    return element.scrollHeight + 48;
  }

  disconnectedCallback() {
    this.detachEvents();
    if (this.closeTimer) clearTimeout(this.closeTimer);
  }

  // ----- Open/Close -----
  openMegaMenu(tab) {
    this.changeTab(tab);
    this.classList.add("open");
    this.overLay.classList.add("open");
    this.setAttribute("aria-hidden", "false");
  }

  closeMegaMenu() {
    this.changeTab();
    this.classList.remove("open");
    this.overLay.classList.remove("open");
    this.setAttribute("aria-hidden", "true");
  }

  maybeCloseMegaMenu() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (!this.isOverOpener && !this.isOverMenu) {
        this.closeMegaMenu();
      }
    }, this.closeDelayMs);
  }

  // Close on scroll movement
  onScroll() {
    const y = window.scrollY || 0;
    if (this.classList.contains("open")) {
      if (Math.abs(y - this._lastScrollY) >= this.scrollCloseThreshold) {
        this.isOverOpener = false;
        this.isOverMenu = false;
        this.closeMegaMenu();
      }
    }
    this._lastScrollY = y;
  }

  changeTab(tab = null) {
    if (this.openTab) this.openTab.classList.remove("open");
    if (!tab) return;
    this.style.height = `${this.getContentHeight(
      tab.querySelector(".height_element")
    )}px`;
    tab.classList.add("open");
    this.openTab = tab;
  }

  // ----- Events -----
  attachEvents() {
    // Hover/focus on openers
    this._openerHandlers = [];
    this.menuOpeners.forEach((opener) => {
      const key = opener.dataset.key;
      const tab = this.menuTabs.filter((tab) => tab.dataset.key === key)[0];
      const onEnter = () => {
        this.isOverOpener = true;
        this.openMegaMenu(tab);
      };
      const onLeave = () => {
        this.isOverOpener = false;
        this.maybeCloseMegaMenu();
      };
      opener.addEventListener("mouseenter", onEnter);
      opener.addEventListener("mouseleave", onLeave);
      opener.addEventListener("focus", onEnter);
      opener.addEventListener("blur", onLeave);
      this._openerHandlers.push([opener, onEnter, onLeave]);
    });

    // Menu hover
    this._onMenuEnter = () => {
      this.isOverMenu = true;
    };
    this._onMenuLeave = () => {
      this.isOverMenu = false;
      this.maybeCloseMegaMenu();
    };
    this.addEventListener("mouseenter", this._onMenuEnter);
    this.addEventListener("mouseleave", this._onMenuLeave);

    // Optional hover area (keeps menu open while moving between opener and menu)
    if (this.hoverArea) {
      this._onHoverEnter = () => {
        this.isOverOpener = true;
      };
      this._onHoverLeave = () => {
        this.isOverOpener = false;
        this.maybeCloseMegaMenu();
      };
      this.hoverArea.addEventListener("mouseenter", this._onHoverEnter);
      this.hoverArea.addEventListener("mouseleave", this._onHoverLeave);
    }

    // Scroll to close
    this._onScroll = this.onScroll;
    window.addEventListener("scroll", this._onScroll, { passive: true });
  }

  detachEvents() {
    // Openers
    if (this._openerHandlers) {
      this._openerHandlers.forEach(([opener, onEnter, onLeave]) => {
        opener.removeEventListener("mouseenter", onEnter);
        opener.removeEventListener("mouseleave", onLeave);
        opener.removeEventListener("focus", onEnter);
        opener.removeEventListener("blur", onLeave);
      });
      this._openerHandlers = null;
    }

    // Menu hover
    if (this._onMenuEnter)
      this.removeEventListener("mouseenter", this._onMenuEnter);
    if (this._onMenuLeave)
      this.removeEventListener("mouseleave", this._onMenuLeave);

    // Hover area
    if (this.hoverArea && this._onHoverEnter && this._onHoverLeave) {
      this.hoverArea.removeEventListener("mouseenter", this._onHoverEnter);
      this.hoverArea.removeEventListener("mouseleave", this._onHoverLeave);
    }

    // Scroll
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      this._onScroll = null;
    }
  }
}

customElements.define("header-megamenu", HeaderMegamenu);

class MobileMegamenu extends HTMLElement {
  constructor() {
    super();

    // State
    this.open = false;
    this.currentOpenLevelTwoKey = null;
  }
  connectedCallback() {
    this.opener = document.querySelector("#mobileMegamenuOpener");
    this.overlay = this.parentElement;
    this.levelOneLi = this.querySelectorAll(
      ".ep__header-mobile-megamenu-first-level"
    );
    this.levelTwoElements = Array.from(this.querySelectorAll(".menu-level-2"));
    this.levelTwoClosers = this.querySelectorAll(".menu-level-2-closer");
    this.letterOpeners = Array.from(
      this.querySelectorAll(".letter-brands-title")
    );
    this.attachEvents();
  }
  openMegamenu() {
    // disable body scroll
    document.body.classList.add("disable-body-scroll");
    this.overlay.classList.add("open");
    this.open = true;
  }
  closeMegamenu() {
    // enable body scroll
    document.body.classList.remove("disable-body-scroll");
    this.overlay.classList.remove("open");
    this.closeLevelTwo();
    this.open = false;
  }
  // Open level two
  openLevelTwo(key) {
    this.levelTwoElements
      .filter((element) => element.dataset.key == key)[0]
      .classList.add("open");
    this.currentOpenLevelTwoKey = key;
  }
  closeLevelTwo() {
    this.levelTwoElements
      .filter(
        (element) => element.dataset.key == this.currentOpenLevelTwoKey
      )[0]
      ?.classList.remove("open");
  }
  closeAllLetters() {
    const svg = opener.querySelector("span > span.letter-brands-chevron");
    this.letterOpeners.forEach((letterOpener) => {
      letterOpener.classList.remov("open");
    });
  }
  attachEvents() {
    this.opener.addEventListener("click", (event) => {
      if (this.open) {
        this.closeMegamenu();
        this.closeAllLetters();
      } else {
        this.openMegamenu();
      }
    });
    this.overlay.addEventListener("click", (event) => {
      if (event.target === event.currentTarget) {
        console.log("overlay click");
        this.closeMegamenu();
      }
    });
    this.levelOneLi.forEach((li) => {
      li.addEventListener("click", (event) => {
        const opener = event.currentTarget.querySelector("svg");
        if (
          event.currentTarget.classList.contains("hasChildren") &&
          event.target.classList.contains(
            "ep__header-mobile-megamenu-first-level-opener"
          )
        ) {
          this.openLevelTwo(li.dataset.key);
        }
      });
    });
    this.levelTwoClosers.forEach((closer) => {
      closer.addEventListener("click", (event) => {
        this.closeLevelTwo();
        this.closeAllLetters();
      });
    });
    this.letterOpeners.forEach((opener) => {
      const svg = opener.querySelector("span > span.letter-brands-chevron");
      opener.addEventListener("click", (event) => {
        event.currentTarget.classList.toggle("open");
        svg.classList.toggle("open");
      });
    });
  }
}

customElements.define("mobile-megamenu", MobileMegamenu);
